/**
 * Apps' "Deploy this service" switch (utils/deployFlags.js): ON for every service by default; OFF for what is not
 * meant to run in production (a design mockup, a tool, a test harness). Saved in `.flow/project.json`: turning it OFF
 * asks first, in one line (like Read-only); ON is saved at once. Editors change it; viewers see it.
 * `NotDeployedBadge`: the services list's tag for a service with Deploy off.
 */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, Switch } from 'antd';
import { CloudSlash } from '@phosphor-icons/react';
import useDeployFlags from '../../hooks/useDeployFlags';
import { DEPLOY_OFF_HELP, DEPLOY_ON_HELP, NOT_DEPLOYED_TIP, deployOffConfirm } from '../../utils/deployFlags';
import '../repoFlags/RepoFlags.css';

export function NotDeployedBadge({ testId = undefined }) {
  return (
    <span className="repo-flags" data-testid={testId}>
      <span aria-label="Not deployed" className="repo-flag is-context" role="img" title={NOT_DEPLOYED_TIP}>
        <CloudSlash aria-hidden size={12} weight="bold" />
        <span aria-hidden>Not deployed</span>
      </span>
    </span>
  );
}

NotDeployedBadge.propTypes = { testId: PropTypes.string };

export default function DeploySwitch({ projectId, service, readOnly = false, compact = false }) {
  const { isDeployed, setDeploy, loaded } = useDeployFlags(projectId);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [asking, setAsking] = useState(false);
  if (!loaded) { return null; }
  const on = isDeployed(service);
  const save = async(next) => {
    setBusy(true);
    setError(null);
    try {
      await setDeploy(service, next);
      setAsking(false);
    } catch (failure) {
      setError(failure?.message || 'The change could not be saved.');
      setAsking(false);
    } finally {
      setBusy(false);
    }
  };
  // OFF asks first (one line); ON goes at once.
  const change = (next) => {
    setError(null);
    if (next) { save(true); } else { setAsking(true); }
  };
  const confirm = asking ? deployOffConfirm(service) : null;
  return (
    <div className="repo-flag-controls apps-deploy" data-testid="apps-deploy">
      <label className={`repo-flag-toggle${readOnly ? ' is-locked' : ''}`} title={readOnly ? 'Only editors can change this' : undefined}>
        <Switch aria-label="Deploy this service" checked={on} data-testid="apps-deploy-switch" disabled={readOnly || busy} onChange={change} size="small" />
        <span>Deploy this service</span>
      </label>
      {!compact && <span className="apps-muted apps-small" data-testid="apps-deploy-help">{on ? DEPLOY_ON_HELP : DEPLOY_OFF_HELP}</span>}
      {error && <span className="repo-flag-error" role="alert">{error}</span>}
      {confirm && (
        <Modal
          cancelText="Cancel"
          className="repo-flag-confirm"
          confirmLoading={busy}
          okText={confirm.ok}
          onCancel={() => { if (!busy) { setAsking(false); } }}
          onOk={() => save(false)}
          open
          title={confirm.title}
        >
          <p data-testid="apps-deploy-confirm">{confirm.line}</p>
        </Modal>
      )}
    </div>
  );
}

DeploySwitch.propTypes = {
  // The switch alone, without the sentence saying what it does.
  compact: PropTypes.bool,
  projectId: PropTypes.string.isRequired,
  readOnly: PropTypes.bool,
  // The short name (`design-mockup`) or `services/<name>`.
  service: PropTypes.string.isRequired,
};
